import { create } from 'zustand';
import { type ApiClient, api } from '~/api/client.js';
import { flight } from '~/session/singleFlight.js';
import { useAuthStore } from './authStore.js';

/**
 * How many people have the app open, and the heartbeat that makes it true.
 *
 * Rails counts a player as online for ten minutes after a signed-in request
 * (`GET /presence`). A player at the table talks to the game service over the
 * websocket and not to Rails, so without a beat of its own they would drop out
 * of the count ten minutes into a game. The beat is the same request the Play
 * screen reads the number from: one call both stamps this player and answers
 * the count.
 *
 * `online` is null until the server has answered and again whenever it could
 * not. The Play screen draws nothing then, rather than a number it cannot
 * stand behind.
 */

/** Under the server's ten-minute window, with its five-minute stamp throttle to spare. */
export const PRESENCE_INTERVAL_MS = 4 * 60_000;

export interface PresenceState {
  /** Injected in tests; production always talks to the shared client. */
  client: ApiClient;
  online: number | null;
  refresh(): Promise<void>;
}

/** A replaceable read: a second caller while one is out gets that answer. */
const asking = flight();

export const usePresenceStore = create<PresenceState>((set, get) => ({
  client: api,
  online: null,
  refresh: () =>
    asking.join(async () => {
      try {
        const { online } = await get().client.presence();
        set({ online });
      } catch {
        set({ online: null });
      }
    }),
}));

/**
 * Beat while the app is in front. Starts with a beat, beats again when the app
 * returns to the foreground and when the session changes (a device that just
 * registered should count at once), and skips a tick while the app is hidden: a
 * phone in a pocket is not a player who is here. Returns the stop.
 */
export function startPresence(): () => void {
  let stopped = false;
  const beat = (): void => {
    if (stopped || document.visibilityState !== 'visible') return;
    void usePresenceStore.getState().refresh();
  };
  beat();

  const timer = setInterval(beat, PRESENCE_INTERVAL_MS);
  document.addEventListener('visibilitychange', beat);
  let token = useAuthStore.getState().token;
  const unsubscribe = useAuthStore.subscribe((state) => {
    if (state.token === token) return;
    token = state.token;
    // Behind any request already out, not joined to it: that one carries the
    // old token, and joining it would leave the new session uncounted until
    // the next tick.
    void asking.idle().then(beat);
  });

  return () => {
    stopped = true;
    clearInterval(timer);
    document.removeEventListener('visibilitychange', beat);
    unsubscribe();
  };
}
